<template>
  <div>
    <div
      v-if="!loaded"
      class="empty_map"
      style="text-align: center"
    >
      <v-progress-circular
        :indeterminate="true"
        size="50"
        style="margin-top: 125px"
      ></v-progress-circular>
    </div>
    <div v-else>
      <GmapMap
        ref="mapRef"
        v-if="topPoints.length !== 0"
        :center="map_center"
        :zoom="map_zoom"
        map-type-id="roadmap"
        :options="mapOptions"
        style="width: calc(100% + 2 * 12px); height: 400px; margin: -12px"
      >
        <gmap-cluster>
          <GmapMarker
            v-for="(point, idx) in topPoints"
            :position="google && new google.maps.LatLng({ lat: point.lat, lng: point.lon })"
            :key="idx"
          ></GmapMarker>
        </gmap-cluster>
      </GmapMap>
      <div class="empty_map" v-else></div>
    </div>
    <v-layout style="margin: 25px 0 30px 0" row>
      <v-flex sm8 xs8 style="text-align: left">
        <h3>
          {{ view_date }}
          <i
            style="margin-left: 5px; cursor: pointer"
            @click="d_date_select = true"
            class="fad fa-calendar-alt"
          ></i>
        </h3>
      </v-flex>
      <v-flex sm4 xs4 style="text-align: right">
        <v-btn @click="monthBack" text>
          <i class="fal fa-chevron-left"></i>
        </v-btn>
        <v-btn
          @click="monthForward"
          :disabled="isCurrentMonth"
          text
        >
          <i class="fal fa-chevron-right"></i>
        </v-btn>
      </v-flex>
    </v-layout>

    <v-row v-if="loaded && locations.length > 0" class="mb-6 mx-0">
      <v-col cols="6" sm="4" class="pa-1" v-if="totalDistance > 0">
        <v-card outlined height="100%">
          <v-card-text style="text-align: center; padding: 12px;">
            <div style="font-size: 11px; color: grey; text-transform: uppercase; font-weight: bold;">{{ $t("timeline.total_distance") }}</div>
            <div style="font-size: 22px; font-weight: bold; margin-top: 4px; color: var(--v-primary-base);">
              <i class="fad fa-route" style="margin-right: 6px;"></i>{{ totalDistance }} <span style="font-size: 14px; font-weight: normal; color: grey;">{{ $t("timeline.km") }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" class="pa-1">
        <v-card outlined height="100%">
          <v-card-text style="text-align: center; padding: 12px;">
            <div style="font-size: 11px; color: grey; text-transform: uppercase; font-weight: bold;">{{ $t("timeline.cities_visited") }}</div>
            <div style="font-size: 22px; font-weight: bold; margin-top: 4px; color: var(--v-primary-base);">
              <i class="fad fa-city" style="margin-right: 6px;"></i>{{ topClusters.length }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" class="pa-1">
        <v-card outlined height="100%">
          <v-card-text style="text-align: center; padding: 12px;">
            <div style="font-size: 11px; color: grey; text-transform: uppercase; font-weight: bold;">{{ $t("timeline.points_recorded") }}</div>
            <div style="font-size: 22px; font-weight: bold; margin-top: 4px; color: var(--v-primary-base);">
              <i class="fad fa-map-pin" style="margin-right: 6px;"></i>{{ locations.length }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <div v-if="!loaded" style="text-align: center">
      <v-progress-circular
        :indeterminate="true"
        size="70"
        style="margin-top: 30px"
      ></v-progress-circular>
    </div>
    <div v-else>
      <div v-if="topClusters.length === 0" style="text-align: center">
        <p>{{ $t("timeline.no_data_recorded_for_this_month_yet") }}</p>
      </div>
      <div v-else>
        <v-list
        style="background: transparent"
        v-if="topClusters.length !== 0"
        two-line>
          <template v-for="(cluster, index) in topClusters">
            <v-list-item :key="index">
              <v-list-item-avatar>
                <span style="font-size: 28px;" v-if="cluster.country && getFlagEmoji(cluster.country)">{{ getFlagEmoji(cluster.country) }}</span>
                <v-icon v-else class="primary white--text">fas fa-city</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ cluster.name || $t("timeline.unknown_city") }}</v-list-item-title>
                <v-list-item-subtitle>{{ cluster.count }} {{ $t("timeline.visits_recorded") }}</v-list-item-subtitle>
                <v-list-item-subtitle v-if="cluster.datesArray && cluster.datesArray.length > 0" style="white-space: normal; margin-top: 4px;">
                  <v-chip
                    v-for="(range, idx) in cluster.datesArray"
                    :key="idx"
                    :to="range.to"
                    small
                    outlined
                    color="primary"
                    class="mr-1 mt-1 mb-1"
                  >
                    {{ range.text }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="cluster.name === $t('timeline.unknown_city') || cluster.country === $t('timeline.unknown_country')">
                <v-btn icon color="primary" @click.stop="retrieveMissingAddresses(cluster)" :title="$t('timeline.retrieve_missing_addresses')">
                  <v-icon>fal fa-map-marked-alt</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index < topClusters.length - 1" :key="'divider-' + index"></v-divider>
          </template>
        </v-list>
        
        <div class="timeline-actions">
          <v-btn
            :to="`/timeline/${programmatically_month}-01`"
            color="default"
            text
          >
            <i class="fal fa-calendar-day" style="margin-right: 5px"></i>
            {{ $t("timeline.daily_view") }}
          </v-btn>
          <v-btn
            :to="`/timeline_year/${programmatically_month.substring(0, 4)}`"
            color="default"
            text
          >
            <i class="fal fa-calendar" style="margin-right: 5px"></i>
            {{ $t("timeline.yearly_view") }}
          </v-btn>
        </div>
      </div>
    </div>

    <v-dialog v-model="d_date_select" max-width="400">
      <v-card>
        <v-card-title class="headline">{{ $t("timeline.select_month") }}</v-card-title>
        <v-card-text style="text-align: center">
          <v-date-picker
            v-model="pickerDate"
            type="month"
            :max="currentMonthStr"
            @change="selectMonth"
          ></v-date-picker>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="refetchProgress.active" persistent max-width="400">
      <v-card>
        <v-card-title class="headline">{{ $t("timeline.notification.retrieving_addresses") || 'Retrieving addresses...' }}</v-card-title>
        <v-card-text>
          <div class="mb-2 text-center" style="font-size: 16px; font-weight: bold;">{{ refetchProgress.current }} / {{ refetchProgress.total }}</div>
          <v-progress-linear
            :value="(refetchProgress.current / refetchProgress.total) * 100"
            height="20"
            color="primary"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import Location from "../logic/Location";
import { storage } from "../schmucklicloud";
import { Notifications } from "../notification";
import { gmapApi } from "vue2-google-maps";
import GmapCluster from "vue2-google-maps/dist/components/cluster";
import { getMapOptions } from "../mapStyles";
Vue.component("GmapCluster", GmapCluster);

export default {
  data() {
    return {
      date: new Date(),
      d_date_select: false,
      loaded: false,
      pickerDate: new Date().toISOString().substr(0, 7),
      totalDistance: 0,
      locations: [],
      topClusters: [],
      topPoints: [],
      refetchProgress: { active: false, current: 0, total: 0 },
    };
  },
  mounted() {
    var current_date = this.$route.params.date;
    if (current_date !== undefined) {
      this.date = new Date(current_date + "-01");
      this.pickerDate = current_date;
    } else {
      this.updateUrl();
    }
    this.load();
  },
  computed: {
    google: gmapApi,
    mapOptions() {
      return getMapOptions(this.$vuetify.theme.dark);
    },
    isCurrentMonth() {
      var today = new Date();
      return (
        this.date.getFullYear() === today.getFullYear() &&
        this.date.getMonth() === today.getMonth()
      );
    },
    currentMonthStr() {
      var today = new Date();
      return today.toISOString().substr(0, 7);
    },
    programmatically_month() {
      return (
        this.date.getFullYear() +
        "-" +
        (this.date.getMonth() + 1 + "").padStart(2, "0")
      );
    },
    view_date() {
      var months = [
        this.$t("general.month.january") || "January",
        this.$t("general.month.february") || "February",
        this.$t("general.month.march") || "March",
        this.$t("general.month.april") || "April",
        this.$t("general.month.may") || "May",
        this.$t("general.month.june") || "June",
        this.$t("general.month.july") || "July",
        this.$t("general.month.august") || "August",
        this.$t("general.month.september") || "September",
        this.$t("general.month.october") || "October",
        this.$t("general.month.november") || "November",
        this.$t("general.month.december") || "December",
      ];
      return months[this.date.getMonth()] + " " + this.date.getFullYear();
    },
    map_zoom() {
      return this.getZoomLevel(this.topPoints);
    },
    map_center() {
      return this.getCenterFromCoordinates(this.topPoints);
    },
  },
  methods: {
    monthBack() {
      this.date = new Date(this.date.getFullYear(), this.date.getMonth() - 1, 1);
      this.pickerDate = this.programmatically_month;
      this.updateUrl();
      this.load();
    },
    monthForward() {
      this.date = new Date(this.date.getFullYear(), this.date.getMonth() + 1, 1);
      this.pickerDate = this.programmatically_month;
      this.updateUrl();
      this.load();
    },
    selectMonth(val) {
      this.date = new Date(val + "-01");
      this.d_date_select = false;
      this.updateUrl();
      this.load();
    },
    updateUrl() {
      if (this.$route.params.date !== this.programmatically_month) {
        this.$router.replace('/timeline_month/' + this.programmatically_month).catch(() => {});
      }
    },
    async load() {
      this.loaded = false;
      this.locations = [];
      this.topClusters = [];
      this.topPoints = [];

      var response = await Location.getMonth(this.programmatically_month);

      if (response.isOK && response.data !== undefined) {
        this.locations = response.data;
        
        this.locations.sort((a, b) => {
            let dateA = a.data.date || "";
            let timeA = a.data.time || "";
            let dateB = b.data.date || "";
            let timeB = b.data.time || "";
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            return timeA.localeCompare(timeB);
        });

        // Anomaly detection
        for (let i = 1; i < this.locations.length - 1; i++) {
          let prev = this.locations[i - 1];
          let curr = this.locations[i];
          let next = this.locations[i + 1];

          let distPrevCurr = this.getDistance(prev.data.lat, prev.data.lon, curr.data.lat, curr.data.lon);
          let distCurrNext = this.getDistance(curr.data.lat, curr.data.lon, next.data.lat, next.data.lon);
          let distPrevNext = this.getDistance(prev.data.lat, prev.data.lon, next.data.lat, next.data.lon);

          if (distPrevCurr > 100000 && distCurrNext > 100000 && distPrevNext < distPrevCurr) {
            curr.anomaly = true;
          }
        }

        var dist = 0;
        for (let i = 1; i < this.locations.length; i++) {
          let prev = this.locations[i - 1];
          let curr = this.locations[i];
          if (!curr.anomaly && !prev.anomaly) {
            let distStep = this.getDistance(prev.data.lat, prev.data.lon, curr.data.lat, curr.data.lon);
            if (distStep < 100000) {
              dist += distStep;
            }
          }
        }
        
        this.totalDistance = (dist / 1000).toFixed(2);
        
        this.processClusters();
      }
      this.loaded = true;
    },
    processClusters() {
      let clusters = {};
      
      this.locations.forEach((loc) => {
        let lat = parseFloat(loc.data.lat);
        let lon = parseFloat(loc.data.lon);
        let address = loc.data.address;
        let date = loc.data.date;

        let name = this.$t("timeline.unknown_city");
        let country = this.$t("timeline.unknown_country");
        if (address && address.trim() !== "-") {
          let parts = address.split(',');
          country = parts[parts.length - 1].replace(/[0-9]/g, '').trim() || this.$t("timeline.unknown_country");
          let cityPart = parts.length > 1 ? parts[parts.length - 2].trim() : parts[0].trim();
          let city = cityPart.replace(/[0-9]/g, '').trim();
          if (city) {
            name = city + (parts.length > 1 ? ", " + country : "");
          }
        }

        if (!clusters[name]) {
          clusters[name] = {
            name: name,
            country: country,
            count: 0,
            points: [],
            dates: new Set(),
            originalLocations: []
          };
        }

        clusters[name].count++;
        clusters[name].points.push({ lat, lon });
        clusters[name].originalLocations.push(loc);
        if (date) {
          let day = parseInt(date.split("-")[2]);
          clusters[name].dates.add(day);
        }
      });

      let monthName = this.date.toLocaleString('default', { month: 'short' });
      let clustersArray = Object.values(clusters).map(c => {
        let daysArray = Array.from(c.dates).sort((a, b) => a - b);
        
        let ranges = [];
        for (let i = 0; i < daysArray.length; i++) {
          let start = daysArray[i];
          let end = start;
          while (i + 1 < daysArray.length && daysArray[i + 1] === daysArray[i] + 1) {
            end = daysArray[i + 1];
            i++;
          }
          let toDateStr = `${this.programmatically_month}-${String(start).padStart(2, '0')}`;
          if (start === end) {
            ranges.push({ text: `${start}. ${monthName}`, to: `/timeline/${toDateStr}` });
          } else {
            let toDateEndStr = `${this.programmatically_month}-${String(end).padStart(2, '0')}`;
            ranges.push({ text: `${start}. ${monthName} - ${end}. ${monthName}`, to: `/timeline/${toDateStr}/${toDateEndStr}` });
          }
        }

        return {
          ...c,
          datesArray: ranges
        };
      });
      clustersArray.sort((a, b) => b.count - a.count);

      this.topClusters = clustersArray;
      
      let points = [];
      this.topClusters.forEach(c => {
        points = points.concat(c.points);
      });
      this.topPoints = points;
    },
    getDistance(lat1, lon1, lat2, lon2) {
      const R = 6371e3; // metres
      const φ1 = (lat1 * Math.PI) / 180;
      const φ2 = (lat2 * Math.PI) / 180;
      const Δφ = ((lat2 - lat1) * Math.PI) / 180;
      const Δλ = ((lon2 - lon1) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c;
    },
    getCenterFromCoordinates(coordinates) {
      if (coordinates.length === 0) return { lat: 0, lng: 0 };
      var lats = 0,
        lngs = 0;
      coordinates.forEach(function (curr) {
        lats += curr.lat;
        lngs += curr.lon;
      });
      return { lat: lats / coordinates.length, lng: lngs / coordinates.length };
    },
    getZoomLevel(coordinates) {
      if (coordinates.length === 0) return 2;
      var GLOBE_WIDTH = 256;
      var pixelWidth = window.innerWidth / 3;
      var pixelHeight = 300;
      var west = 180,
        east = -180,
        north = 180,
        south = -180;
      coordinates.forEach(function (curr) {
        if (curr.lon < west) {
          west = curr.lon;
        }
        if (curr.lon > east) {
          east = curr.lon;
        }

        if (curr.lat < north) {
          north = curr.lat;
        }
        if (curr.lat > south) {
          south = curr.lat;
        }
      });
      var angle = east - west;
      if (angle < 0) {
        angle += 360;
      }

      var angle2 = south - north;
      if (angle2 < 0) {
        angle2 += 360;
      }
      
      if (angle === 0 && angle2 === 0) return 15;

      return (
        Math.round(
          (Math.round(
            Math.log((pixelWidth * 360) / angle / GLOBE_WIDTH) / Math.LN2
          ) +
            Math.round(
              Math.log((pixelHeight * 360) / angle2 / GLOBE_WIDTH) / Math.LN2
            )) /
            2
        ) - 2
      );
    },
    async retrieveMissingAddresses(cluster) {
      if (!confirm(this.$t("timeline.do_you_really_want_to_retrieve_missing_addresses"))) {
        return;
      }

      let missingAddressLocations = cluster.originalLocations.filter(loc => !loc.data.address || loc.data.address.trim() === "-");
      this.refetchProgress = { active: true, current: 0, total: missingAddressLocations.length };
      
      for (let loc of missingAddressLocations) {
        let delete_response = await storage.delete("locations", loc.id);
        if (delete_response.isOK) {
          await storage.insert("locations", loc.data);
        }
        this.refetchProgress.current++;
      }
      
      this.refetchProgress.active = false;
      Notifications.show(this.$t("timeline.notification.addresses_retrieved"));
      setTimeout(() => {
        this.load();
      }, 2000);
    },
  },
};
</script>

<style scoped>
.empty_map {
  height: 400px;
  width: calc(100% + 2 * 12px);
  margin: -12px;
  background: grey;
}

.timeline-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px 0;
}

.timeline-actions .v-btn {
  width: 100%;
}

@media (min-width: 600px) {
  .timeline-actions {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .timeline-actions .v-btn {
    width: auto;
  }
}
</style>
